// must be first
import '@js-joda/locale_en-us'
import '@js-joda/timezone-10-year-range'

import { CommonModule } from '@angular/common'
import { HTTP_INTERCEPTORS, provideHttpClient, withInterceptorsFromDi } from '@angular/common/http'
import {
  EnvironmentProviders,
  ErrorHandler,
  inject,
  NgModule,
  provideAppInitializer,
  Provider,
  signal,
} from '@angular/core'
import { JWT_OPTIONS, JwtModule } from '@auth0/angular-jwt'
import {
  API_CONFIGURATION,
  AUTH_CONFIGURATION,
  AUTH_TOKEN_FETCHER,
  STANDALONE_APP_CONFIGURATION,
} from '@ftr/api-shared'
import { AppMode } from '@ftr/contracts/api/user'
import { CourtSystemsState } from '@ftr/data-court-system'
import {
  BreadcrumbModule,
  ConfirmationModalComponent,
  DialogComponent,
  IllustrationComponent,
  isInDisplayApp,
  KonamiDirective,
  LayoutService,
  OnClickOutsideDirective,
  PageComponent,
  PortalComponent,
  SimpleWindowRefService,
  SizesService,
  TippyComponent,
  ToastModule,
  TrackingService,
} from '@ftr/foundation'
import { APP_MODE } from '@ftr/routing-paths'
import { PostLoginNotificationComponent } from '@ftr/ui-audit'
import {
  AppNavigationLoaderComponent,
  AuthRedirectInterceptor,
  CoreModule,
  FooterComponent,
  jwtOptionsFactory,
  LaunchDarklyAttributeService,
  LoggingInterceptor,
  MfaRequirementsState,
  UnderConstructionComponent,
} from '@ftr/ui-core'
import { DesktopModule } from '@ftr/ui-desktop'
import { FeatureFlagState, LAUNCH_DARKLY_CONFIG, LaunchDarklyModule, LaunchDarklyService } from '@ftr/ui-feature-flags'
import { AppDisplayComponent } from '@ftr/ui-header'
import { CourtroomState } from '@ftr/ui-location'
import { ErrorHandlerService, OBSERVABILITY_CONFIGURATION } from '@ftr/ui-observability'
import { OrderState } from '@ftr/ui-ordering'
import { HeaderSizeService, PlaybackState } from '@ftr/ui-playback'
import { SearchManyState } from '@ftr/ui-search'
import {
  AnalyticsService,
  AppLoaderComponent,
  AuthenticationService,
  AuthTokenFetcherService,
  FavoritesState,
  MfaSetupModalComponent,
  OrderAccountState,
  PortalDomainService,
  UseConditionsState,
  UserState,
} from '@ftr/ui-user'
import { VocabularyState } from '@ftr/ui-vocab'
import { WindowRefService } from '@ftr/window'
import { withNgxsReduxDevtoolsPlugin } from '@ngxs/devtools-plugin'
import { provideStore } from '@ngxs/store'
import { provideAngularSvgIcon, SvgIconComponent } from 'angular-svg-icon'
import { HttpDecodeInterceptor } from '~app/services/http-client/http-decode-interceptor'
import { AppLayoutService } from '~app/services/layout/app-layout.service'
import { environment } from '~environments/environment'
import { NGXS_OPTIONS } from '~store/ngxs-options'
import { AppRoutingModule } from './app-routing.module'
import { AppComponent } from './app.component'
import { ConfigurationService } from './services/configuration/configuration.service'

/**
 * Holds the resolved {@link AppMode}. Populated by the APP_INITIALIZER below before bootstrap
 * completes. A signal is used (rather than a plain primitive) so that consumers always read the
 * current value: providing a primitive via `useFactory` would cache the default value at the
 * point of first injection (which can occur during eager NGXS state construction, before the
 * initializer runs).
 */
const appModeSignal = signal<AppMode>('legacy')

// Application wide providers
const APP_PROVIDERS: (Provider | EnvironmentProviders)[] = [
  { provide: APP_MODE, useValue: appModeSignal },
  provideAppInitializer(async () => {
    const portalDomainService = inject(PortalDomainService)
    const launchDarklyService = inject(LaunchDarklyService)

    if (portalDomainService.isCourtsPortal()) {
      appModeSignal.set('courts')
    } else if (
      portalDomainService.isSitePortal() &&
      (await launchDarklyService.getFlagVariationForAnonymous<boolean>('public-navigation'))
    ) {
      appModeSignal.set('portal')
    } else {
      appModeSignal.set('legacy')
    }
  }),
  { provide: ErrorHandler, useClass: ErrorHandlerService },
  { provide: TrackingService, useClass: AnalyticsService },
  { provide: SimpleWindowRefService, useClass: WindowRefService },
  { provide: SizesService, useClass: HeaderSizeService },
  { provide: LayoutService, useClass: AppLayoutService },
  { provide: LAUNCH_DARKLY_CONFIG, useClass: ConfigurationService },
  { provide: API_CONFIGURATION, useClass: ConfigurationService },
  { provide: AUTH_CONFIGURATION, useClass: ConfigurationService },
  { provide: STANDALONE_APP_CONFIGURATION, useClass: ConfigurationService },
  { provide: OBSERVABILITY_CONFIGURATION, useClass: ConfigurationService },
  { provide: AUTH_TOKEN_FETCHER, useClass: AuthTokenFetcherService },
  // Wire up launch darkly to apply attributes globally from the store
  provideAppInitializer(() => {
    const initializerFn = ((service: LaunchDarklyAttributeService) => () => {
      if (isInDisplayApp()) {
        // Don't apply user based feature flag setup in display app
        return
      }
      service.observeAndApplyUserAttributes()
    })(inject(LaunchDarklyAttributeService))
    return initializerFn()
  }),
  // Wire up analytics service to observe page views and auth events (user login/logout)
  provideAppInitializer(() => {
    const initializerFn = ((service: AnalyticsService) => () => {
      if (isInDisplayApp()) {
        // Don't apply GA setup for display app
        return
      }
      service.observePageViewsAndAuthEvents()
    })(inject(AnalyticsService))
    return initializerFn()
  }),
  provideAngularSvgIcon(),
]

const httpInterceptorProviders = [
  {
    provide: HTTP_INTERCEPTORS,
    useClass: AuthRedirectInterceptor,
    multi: true,
  },
  { provide: HTTP_INTERCEPTORS, useClass: LoggingInterceptor, multi: true },
  { provide: HTTP_INTERCEPTORS, useClass: HttpDecodeInterceptor, multi: true },
]

/**
 * `AppModule` is the main entry point into Angular's bootstrap process
 */
@NgModule({
  bootstrap: [AppComponent],
  declarations: [AppComponent],
  imports: [
    CommonModule,
    AppDisplayComponent,
    AppLoaderComponent,
    AppRoutingModule,
    JwtModule.forRoot({
      jwtOptionsProvider: {
        provide: JWT_OPTIONS,
        useFactory: jwtOptionsFactory,
        deps: [AuthenticationService, ConfigurationService],
      },
    }),
    CoreModule.forRoot(),
    AppRoutingModule,
    BreadcrumbModule,
    ToastModule,
    DesktopModule,
    MfaSetupModalComponent,
    PostLoginNotificationComponent,
    PortalComponent,
    PageComponent,
    LaunchDarklyModule.forRoot(),
    DialogComponent,
    ConfirmationModalComponent,
    FooterComponent,
    KonamiDirective,
    IllustrationComponent,
    TippyComponent,
    OnClickOutsideDirective,
    AppNavigationLoaderComponent,
    SvgIconComponent,
    UnderConstructionComponent,
  ],
  providers: [
    APP_PROVIDERS,
    httpInterceptorProviders,
    provideHttpClient(withInterceptorsFromDi()),
    /**
     * Only register states here if they are required globally — needed by
     * app-level components or widely used across unrelated features (e.g. feature flag, vocab).
     *
     * States registered here are ALWAYS included in the main bundle, regardless
     * of whether the user ever visits the feature that uses them.
     *
     * For feature states, register them closer to where they are used
     * via providers: [provideStates([...])] in lazy-loaded modules or routes.
     */
    provideStore(
      [
        UserState,
        SearchManyState,
        PlaybackState,
        CourtSystemsState,
        VocabularyState,
        OrderState,
        OrderAccountState,
        CourtroomState,
        FeatureFlagState,
        UseConditionsState,
        MfaRequirementsState,
        FavoritesState,
      ],
      NGXS_OPTIONS,
      withNgxsReduxDevtoolsPlugin({
        disabled: environment.production,
        name: 'FTR-WEB',
        // These are super noisy and polling, essentially making the devtools unusable
        actionsBlacklist: ['\\[Annotations\\] Set Live LocalTime', '\\[Playback\\] Reevaluate on record state'],
      }),
    ),
  ],
})
export class AppModule {}
